import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { type FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Spinner from '@/components/ui/spinner';
import { getEstateOptions } from '@/features/estates-property/api/estate';

import { useUpdateEstateInfo } from '../api/estate-info';
import type { EstateValidationSchemaType, transformEstate } from '../schemas/estate-validation';
import { getEstateValidationSchema } from '../schemas/estate-validation';
import { AreasForm } from './analytics-request-forms/areas-form';
import { EnergyForm } from './analytics-request-forms/energy-form';
import { LocationForm } from './analytics-request-forms/location-form';
import { PropertyDetailsForm } from './analytics-request-forms/property-details-form';
import { RatingsForm } from './analytics-request-forms/ratings-form';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/analytics'
);

const getFirstErrorMessage = (
  errors: FieldErrors<EstateValidationSchemaType>
): string | undefined => {
  for (const error of Object.values(errors)) {
    if (!error) {
      continue;
    }

    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }

    if (typeof error === 'object') {
      const nestedMessage = getFirstErrorMessage(error as FieldErrors<EstateValidationSchemaType>);

      if (nestedMessage) {
        return nestedMessage;
      }
    }
  }

  return undefined;
};

export const AnalyticsRequestForm: React.FC<{
  estateData: EstateValidationSchemaType | ReturnType<typeof transformEstate>;
  onSaved: (data: EstateValidationSchemaType) => void;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
}> = ({ estateData, onSaved, setIsEditing }) => {
  const { t } = useTranslation();
  const estate = route.useLoaderData();
  const queryClient = useQueryClient();
  const router = useRouter();

  const { mutateAsync, isPending } = useUpdateEstateInfo({
    mutationConfig: {
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const handleSubmit = async (data: EstateValidationSchemaType) => {
    await mutateAsync({ estate, updatedData: data });
    await queryClient.invalidateQueries({ queryKey: getEstateOptions(estate.id).queryKey });
    await router.invalidate();
    onSaved(data);
    toast.success(t('update-estate-success'));
    setIsEditing(false);
  };

  const handleInvalidSubmit = (errors: FieldErrors<EstateValidationSchemaType>) => {
    toast.error(getFirstErrorMessage(errors) ?? t('fillFormToGenerateAnalytics'));
  };

  const methods = useForm<EstateValidationSchemaType>({
    resolver: zodResolver(getEstateValidationSchema()),
    defaultValues: estateData as EstateValidationSchemaType,
    shouldUseNativeValidation: false,
  });

  const { clearErrors, setValue, watch } = methods;

  const propertyType = watch('estateDetails.propertyType');

  useEffect(() => {
    if (propertyType === 'apartment') {
      setValue('estateAreasRooms.plotArea', undefined, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      clearErrors('estateAreasRooms.plotArea');
    } else if (propertyType === 'house') {
      setValue('estateAreasRooms.hasElevator', undefined, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      setValue('estateAreasRooms.floor', undefined, {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      });
      clearErrors(['estateAreasRooms.hasElevator', 'estateAreasRooms.floor']);
    }
  }, [clearErrors, setValue, propertyType]);

  return (
    <Card className="rounded-md p-6 text-left">
      <h3 className="text-[16px] font-bold">{t('analyticsNotAvailable')}</h3>
      <p className="text-sm text-text-secondary">{t('fillFormToGenerateAnalytics')}</p>
      <FormProvider {...methods}>
        <form
          className="space-y-6"
          noValidate
          onSubmit={methods.handleSubmit(handleSubmit, handleInvalidSubmit)}
        >
          <div className="relative mt-4 grid grid-cols-2 gap-x-20">
            <div className="space-y-8">
              <LocationForm />
              <AreasForm />
            </div>
            <div className="space-y-8">
              <PropertyDetailsForm />
              <EnergyForm />
              <RatingsForm />
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button
              type="submit"
              variant="contained"
              size="md"
              className="!px-12"
              disabled={isPending}
            >
              {isPending ? <Spinner className="mr-2 !size-5" /> : null}
              {t('save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Card>
  );
};
